import { ActionFunctionArgs } from "@remix-run/cloudflare";
import { Form, useActionData, useLocation } from "@remix-run/react";
import { drizzle } from "drizzle-orm/d1";
import { dayjs } from "~/utils/dayjs";

import * as schema from "~/db/schema.server";
import { Button } from "~/ui/Button";
import { Input } from "~/ui/Input";
import { buildConflictUpdateColumns } from "~/db/models/utils.server";
import { generateId } from "lucia";
import { generateResetTokens } from "~/db/models/auth.server";
import { getEmailTemplate, sendEmailTemplate } from "~/db/models/email.server";

export default function ForgotPassword() {
  const action = useActionData<typeof action>();
  const { state } = useLocation();
  return (
    <Form method="POST" className="flex flex-col gap-4 max-w-sm w-full mx-auto mt-4">
      <div>
        <h2 className="font-title text-2xl mb-1">Forgot password</h2>
        <p className="text-subtle">
          Enter the email address you signed up with, and we&apos;ll send you instructions on how to reset your
          password.
        </p>
      </div>
      <label className="flex flex-col gap-1" htmlFor="email">
        e-mail
        <Input type="email" name="email" id="email" placeholder="Email" required defaultValue={state?.email || ""} />
      </label>
      <div className="flex flex-col items-center gap-y-2">
        <Button type="submit">Send instructions</Button>
      </div>
      {action?.ok ? (
        <div className="bg-success bg-opacity-30 border-2 border-success rounded-md py-2 px-4">
          Instructions have been sent to your email
        </div>
      ) : null}
    </Form>
  );
}

export async function action({ request, context }: ActionFunctionArgs) {
  const db = drizzle(context.cloudflare.env.DB, { schema, logger: false });

  const body = await request.formData();

  const email = body.get("email") as string;

  const existingUser = await db.query.user.findFirst({
    where: (user, { eq }) => eq(user.email, email),
  });

  if (existingUser) {
    const [tokenId, tokenHash] = await generateResetTokens();
    await db
      .insert(schema.authResetToken)
      .values({
        id: generateId(10),
        userId: existingUser.id,
        tokenHash,
        expiresAt: dayjs().add(2, "hours").toISOString(),
      })
      .onConflictDoUpdate({
        target: [schema.authResetToken.userId],
        set: buildConflictUpdateColumns(schema.authResetToken, ["tokenHash", "expiresAt"]),
      });

    const url = new URL(request.url);
    const resetPasswordUrl = `${url.origin}/reset?token=${tokenId}`;

    const emailTemplate = getEmailTemplate("resetPassword")!;
    emailTemplate.props = { resetPasswordUrl };

    await sendEmailTemplate(context.cloudflare.env, emailTemplate, existingUser.email, {
      sendEmail: true,
      preview: false,
    });
  }

  return {
    ok: true,
  };
}
